import { useEffect, useRef } from "react";

/**
 * useKeyboardShortcuts — Vague 6.
 *
 * Global keyboard listener for the interactive list.
 *   1 → set status "no_answer" on current row
 *   2 → set status "refused"
 *   3 → set status "callback"
 *   4 → set status "interested"
 *   5 → set status "voicemail"
 *   6 → set status "wrong_number"
 *   N → jump to next `to_call` row
 *   Space → toggle assist panel of current row
 *   ? → show the shortcuts help modal
 *
 * The "current row" is the FIRST visible row still in `to_call`, OR the
 * currently-scrolled-into-view row (whichever comes first). Ignored when
 * the user is typing inside a textarea / input / contenteditable.
 */
export function useKeyboardShortcuts({ enabled = true, onAction }) {
  const handlerRef = useRef(onAction);
  handlerRef.current = onAction;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e) => {
      // Ignore when typing in an input, textarea, contenteditable, or with modifier keys
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select"
          || e.target?.isContentEditable) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const key = e.key.toLowerCase();
      const map = {
        "1": "no_answer",
        "2": "refused",
        "3": "callback",
        "4": "interested",
        "5": "voicemail",
        "6": "wrong_number",
      };
      if (map[key]) {
        e.preventDefault();
        handlerRef.current?.({ type: "set-status", status: map[key] });
        return;
      }
      if (key === "n") {
        e.preventDefault();
        handlerRef.current?.({ type: "next" });
      } else if (key === " ") {
        e.preventDefault();
        handlerRef.current?.({ type: "toggle-assist" });
      } else if (key === "?") {
        e.preventDefault();
        handlerRef.current?.({ type: "help" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
}
